import { useMemo } from 'react'

import { MainContent, ModalContext, TopBar } from '@domain-group/suit-theme'
import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useMainNavigation from '@hooks/useMainNavigation'

import handleCallbackAction from '@functions/handleCallbackAction'

import CreateOrEditTaskListSectionModal from '@modals/CreateOrEditTaskListSectionModal'
import CreateOrEditTaskListItemModal from '@modals/CreateOrEditTaskListItemModal'

import useTaskList from '@hooks/useTaskList'

import DashboardSearch from '@components/DashboardSearch'
import LoggedInUser from '@components/LoggedInUser'
import MainNavigation from '@sections/Admin/components/MainNavigation'

import defaultRequestOptions from '../../defaultRequestOptions'

import ProjectDetailRoutes from './routes'

import Navigation from './components/Navigation'

export type PageContextPayload = {
  callbacks: {
    showCreateOrEditTaskListItemModal: (payload: any) => void,
    showCreateOrEditTaskListSectionModal: (payload: any) => void,
    showViewRequestedChangesModal: (payload: any) => void,
  },
}

const callbacks = (component: string, setState) => {
  const componentCallbacks = {
    CreateOrEditTaskListItemModal: {
      closeModal: () => setState({ showCreateOrEditTaskListItemModal: false }),
      createTaskListItem: payload => handleCallbackAction(payload),
      deleteTaskListItem: payload => handleCallbackAction(payload),
      updateTaskListItem: payload => handleCallbackAction(payload),
    },
    CreateOrEditTaskListSectionModal: {
      closeModal: () => setState({ showCreateOrEditTaskListSectionModal: false }),
      createTaskListSection: payload => handleCallbackAction(payload),
      updateTaskListSection: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showCreateOrEditTaskListItemModal: false,
  showCreateOrEditTaskListSectionModal: false,
}

const ProjectDetail = () => {
  const { taskListId } = useNumericParams()

  const { callbacks: { toggleNavigation } } = useMainNavigation()

  useTaskList({ id: taskListId }, {
    performHttpRequests: !!taskListId,
    requestOptions: {
      ...defaultRequestOptions.taskList,
      include_related_entity_subject: true,
      include_task_list_related_entities: true,
      include_task_list_sections: true,
      include_task_list_task_counts: true,
    },
  })

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditTaskListItemModal,
    showCreateOrEditTaskListSectionModal,
  } = state

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateOrEditTaskListSectionModal: (payload: any) => {
        setModalData('CreateOrEditTaskListSectionModal', payload)
        setState({ showCreateOrEditTaskListSectionModal: true })
      },
      showCreateOrEditTaskListItemModal: (payload: any) => {
        setModalData('CreateOrEditTaskListItemModal', payload)
        setState({ showCreateOrEditTaskListItemModal: true })
      },
    },
  }), [])

  return (
    <>
      <TopBar
        callbacks={{ toggleNavigation }}
        loggedInUserComponent={<LoggedInUser />}
        nestedNavigation
        searchComponent={(
          <DashboardSearch
            searchEntities={['LayoutTemplate', 'TaskList', 'TemplatePage', 'TemplatePagePartial']}
          />
        )}
      />

      <MainNavigation minimized nestedNavigation={<Navigation taskListId={taskListId} />} />

      <MainContent nestedNavigation offset={{ top: TopBar.topBarHeight }}>
        <PageContext.Provider value={pageContext}>
          <ModalContext.Provider value={modalContext}>
            <ProjectDetailRoutes taskListId={taskListId} />

            <CreateOrEditTaskListSectionModal
              callbacks={callbacks('CreateOrEditTaskListSectionModal', setState)}
              showModal={showCreateOrEditTaskListSectionModal}
            />

            <CreateOrEditTaskListItemModal
              callbacks={callbacks('CreateOrEditTaskListItemModal', setState)}
              showModal={showCreateOrEditTaskListItemModal}
            />
          </ModalContext.Provider>
        </PageContext.Provider>
      </MainContent>
    </>
  )
}

export default ProjectDetail
